/**
 * TOSS Web Application — Client Portal: session-wide realtime (release Р4, decision Р20)
 *
 * The `CompanySwitched` half of the company switcher — the part that keeps every OTHER
 * open tab (and this one, when an administrator revoked the active company rather than
 * the customer switching it themselves) from going on rendering the previous company's
 * data. `useClientChatRealtime.ts` and `useClientBadges.ts` cover the chat and the two
 * unread counters; this hook is deliberately narrower — session-level events that are not
 * about any one screen.
 *
 * **Mounted in `ClientLayout`, not in a page hook** (contract-decisions §2 remark 7).
 * `useClientChatRealtime` is mounted only by `ClientMessengerPage` and
 * `useClientNotificationsRealtime` only by `ClientNotificationsPage` — neither runs on a
 * tab sitting on `/client/documents`, which would then never learn its company changed.
 * `ClientLayout` wraps every authenticated portal route, so this hook does too.
 *
 * Subscribes to the SAME `/hubs/notifications` singleton `useRealtimeNotifications()`
 * already keeps connected for every authenticated session (`app/providers.tsx`) — this
 * hook only calls `.on()`, never `startConnection()` / `stopConnection()`, exactly like
 * {@link useClientNotificationsRealtime}.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { signalRManager } from '@/realtime';
import { useAuthStore } from '@/store/authStore';
import { UserRole } from '@/types';
import { NotificationHubMethods } from '@/types/notification.types';
import { applyCompanySwitch, discardForeignRouteOnCompanySwitch } from './clientCompanySwitch';

/** Whether realtime may run: an authenticated session whose role is `Client`. */
function useIsClientSession(): boolean {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isClient = useAuthStore((state) => state.user?.role === UserRole.Client);
  return isAuthenticated && isClient;
}

/**
 * Keeps the portal's session-level state (query cache, `authStore`, the messenger socket,
 * the current route) in step with which company the account acts for.
 *
 * Renders nothing and returns nothing — call it once, for its effect, exactly like
 * {@link useClientNotificationsRealtime}.
 *
 * @example
 * ```tsx
 * export function ClientLayout({ children }: ClientLayoutProps): JSX.Element {
 *   useClientSessionRealtime();
 *   // ...
 * }
 * ```
 */
export function useClientSessionRealtime(): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientSession = useIsClientSession();

  // Latest-ref, updated after every render: the `CompanySwitched` handler below must read
  // whichever path is on screen AT THE MOMENT the event fires, which could be long after
  // this effect last ran. Re-subscribing on every navigation (a `pathname` dependency
  // instead) would work too, but would tear down and rebuild the subscription on every
  // route change in the whole portal for no behavioural gain — the same "ref instead of a
  // churny dependency" trade `useClientChatRealtime.ts`'s `useDebouncedInvalidate` makes.
  const pathnameRef = useRef(location.pathname);
  const searchRef = useRef(location.search);
  useEffect(() => {
    pathnameRef.current = location.pathname;
    searchRef.current = location.search;
  });

  useEffect(() => {
    if (!isClientSession) return;

    // The event's own `customerId` payload is deliberately unread here, the same way
    // `useClientNotificationsRealtime` ignores its payload: `applyCompanySwitch` re-fetches
    // `/users/me` itself, which also carries the fresh `availableCompanies` — a field the
    // bare push does not, and the header switcher needs it just as much as the id.
    return signalRManager.on<string>(NotificationHubMethods.CompanySwitched, () => {
      // Navigate FIRST, synchronously — `applyCompanySwitch`'s cache clear below is what
      // would otherwise let a still-mounted detail page's own query race-answer 404 under
      // the new company before this effect gets to redirect it away. See
      // `discardForeignRouteOnCompanySwitch`'s own doc for the full race.
      discardForeignRouteOnCompanySwitch(navigate, pathnameRef.current, searchRef.current);
      void applyCompanySwitch({ queryClient });
    });
  }, [isClientSession, queryClient, navigate]);
}

export default useClientSessionRealtime;
